import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { StoreProvider } from "@/contexts/StoreContext";
import { WishlistProvider } from "@/contexts/WishlistContext";
import { RecentlyViewedProvider } from "@/contexts/RecentlyViewedContext";
import { Toaster } from "sonner";

import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";
import AdminLandingRedirect from "@/components/AdminLandingRedirect";
import CustomerShell from "@/components/customer/CustomerShell";
import CustomerProtectedRoute from "@/components/customer/CustomerProtectedRoute";
import Splash from "@/components/customer/Splash";

/* Admin */
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import MasterProducts from "@/pages/MasterProducts";
import Categories from "@/pages/Categories";
import Brands from "@/pages/Brands";
import Inventory from "@/pages/Inventory";
import Suppliers from "@/pages/Suppliers";
import Purchases from "@/pages/Purchases";
import Warehouses from "@/pages/Warehouses";
import Employees from "@/pages/Employees";
import CustomersAdmin from "@/pages/Customers";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import StoreSettings from "@/pages/StoreSettings";
import Profile from "@/pages/Profile";
import Reports from "@/pages/Reports";
import Marketing from "@/pages/Marketing";

/* Delivery */
import DeliveryProtectedRoute from "@/components/delivery/DeliveryProtectedRoute";
import DeliveryShell from "@/components/delivery/DeliveryShell";
import Runs from "@/pages/delivery/Runs";
import DeliveryOrderDetail from "@/pages/delivery/OrderDetail";
import DeliveryHistory from "@/pages/delivery/History";
import Earnings from "@/pages/delivery/Earnings";
import DeliveryProfile from "@/pages/delivery/Profile";
import DeliveryNotifications from "@/pages/delivery/Notifications";

/* Customer */
import Home from "@/pages/customer/Home";
import Shop from "@/pages/customer/Shop";
import PDP from "@/pages/customer/ProductDetail";
import Cart from "@/pages/customer/Cart";
import Checkout from "@/pages/customer/Checkout";
import OrderSuccess from "@/pages/customer/OrderSuccess";
import MyOrders from "@/pages/customer/MyOrders";
import CustomerOrderDetail from "@/pages/customer/CustomerOrderDetail";
import CustomerProfile from "@/pages/customer/Profile";
import About from "@/pages/customer/About";
import Contact from "@/pages/customer/Contact";
import Privacy from "@/pages/customer/Privacy";
import CategoryBrowse from "@/pages/customer/CategoryBrowse";
import Wishlist from "@/pages/customer/Wishlist";
import DealBazaar from "@/pages/customer/DealBazaar";

/* Admin — new Sprint 8 pages */
import HomepageLayout from "@/pages/HomepageLayout";
import WebsiteBuilder from "@/pages/WebsiteBuilder";
import StoreStatus from "@/pages/StoreStatus";
import DeliveryZones from "@/pages/DeliveryZones";
import Stores from "@/pages/Stores";
import UICustomization from "@/pages/UICustomization";
import VisualBuilder from "@/pages/VisualBuilder";
import BulkProductUpload from "@/pages/BulkProductUpload";
import AdminPOS from "@/pages/AdminPOS";
import POSInvoices from "@/pages/POSInvoices";
import DealBazaarAdmin from "@/pages/DealBazaarAdmin";
import Advertisement from "@/pages/Advertisement";

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <StoreProvider>
          <WishlistProvider>
            <RecentlyViewedProvider>
              <CartProvider>
                <Toaster position="top-right" richColors closeButton />
                <Splash />
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />

                  {/* Customer public + protected — shares one shell */}
                  <Route element={<CustomerShell />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/shop" element={<Shop />} />
                    {/* Sprint — Blinkit-style /search alias reusing the shop grid */}
                    <Route path="/search" element={<Shop />} />
                    <Route path="/category" element={<CategoryBrowse />} />
                    <Route path="/category/:slug" element={<CategoryBrowse />} />
                    <Route path="/wishlist" element={<Wishlist />} />
                    <Route path="/deal-baazar" element={<DealBazaar />} />
                    <Route path="/deal-baazar/:slug" element={<DealBazaar />} />
                    {/* Sprint 65.2 — Back-compat: old /deal-bazaar links keep working */}
                    <Route path="/deal-bazaar" element={<DealBazaar />} />
                    <Route path="/deal-bazaar/:slug" element={<DealBazaar />} />
                    <Route path="/product/:id" element={<PDP />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/privacy" element={<Privacy />} />

                    {/* Requires customer session */}
                    {/* Sprint — Guest checkout: /cart is public. Auth is enforced
                        only at /checkout, so guests can browse + build a cart. */}
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/checkout" element={<CustomerProtectedRoute><Checkout /></CustomerProtectedRoute>} />
                    <Route path="/order-success/:id" element={<CustomerProtectedRoute><OrderSuccess /></CustomerProtectedRoute>} />
                    <Route path="/account/orders" element={<CustomerProtectedRoute><MyOrders /></CustomerProtectedRoute>} />
                    <Route path="/account/orders/:id" element={<CustomerProtectedRoute><CustomerOrderDetail /></CustomerProtectedRoute>} />
                    <Route path="/account" element={<CustomerProtectedRoute><CustomerProfile /></CustomerProtectedRoute>} />
                  </Route>

                  {/* Delivery Partner app */}
                  <Route path="/delivery" element={
                    <DeliveryProtectedRoute>
                      <DeliveryShell />
                    </DeliveryProtectedRoute>
                  }>
                    <Route index element={<Runs />} />
                    <Route path="order/:id" element={<DeliveryOrderDetail />} />
                    <Route path="history" element={<DeliveryHistory />} />
                    <Route path="earnings" element={<Earnings />} />
                    <Route path="notifications" element={<DeliveryNotifications />} />
                    <Route path="profile" element={<DeliveryProfile />} />
                  </Route>

                  {/* ─── Admin shell (staff only, store-slug scoped) ─────
                      Phase 3 URL contract:
                        /admin/{store-slug}/dashboard, /orders, /pos, etc.
                      Legacy `/admin/*` routes without a slug redirect to the
                      user's own slug (or first store for SUPER_ADMIN).
                      AppShell enforces cross-tenant access. */}
                  <Route path="/admin" element={
                    <ProtectedRoute>
                      <AdminLandingRedirect />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/:storeSlug" element={
                    <ProtectedRoute>
                      <AppShell />
                    </ProtectedRoute>
                  }>
                    <Route index element={<AdminLandingRedirect />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="pos" element={<AdminPOS />} />
                    <Route path="pos/invoices" element={<POSInvoices />} />
                    <Route path="products" element={<Products />} />
                    <Route path="master-products" element={<MasterProducts />} />
                    <Route path="bulk-product-upload" element={<BulkProductUpload />} />
                    <Route path="categories" element={<Categories />} />
                    <Route path="brands" element={<Brands />} />
                    <Route path="inventory" element={<Inventory />} />
                    <Route path="suppliers" element={<Suppliers />} />
                    <Route path="purchases" element={<Purchases />} />
                    <Route path="warehouses" element={<Warehouses />} />
                    <Route path="employees" element={<Employees />} />
                    <Route path="stores" element={<Stores />} />
                    <Route path="ui-customization" element={<UICustomization />} />
                    <Route path="visual-builder" element={<VisualBuilder />} />
                    <Route path="customers" element={<CustomersAdmin />} />
                    <Route path="orders" element={<Orders />} />
                    <Route path="orders/:id" element={<OrderDetail />} />
                    <Route path="marketing" element={<Marketing />} />
                    <Route path="advertisement" element={<Advertisement />} />
                    <Route path="deal-baazar" element={<DealBazaarAdmin />} />
                    {/* Sprint 65.2 — Back-compat alias */}
                    <Route path="deal-bazaar" element={<DealBazaarAdmin />} />
                    <Route path="homepage-layout" element={<HomepageLayout />} />
                    <Route path="website-builder" element={<WebsiteBuilder />} />
                    <Route path="store-status" element={<StoreStatus />} />
                    <Route path="delivery-zones" element={<DeliveryZones />} />
                    <Route path="store-settings" element={<StoreSettings />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="reports" element={<Reports />} />
                  </Route>

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </CartProvider>
            </RecentlyViewedProvider>
          </WishlistProvider>
        </StoreProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
